// libs
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import isEmpty from 'lodash/isEmpty';

// global components
import ToastProvider from 'shared/components/Toast';
import LoginNudgeProvider from 'shared/components/Nudge';
import { useCartNudge } from 'shared/layouts/DetailsPage/components/CartNudgeProvider';
import { getEligibleVariantName } from 'shared/components/ExperimentV2/helpers';

// components
import Title from 'shared/layouts/DetailsPage/components/ProductInfo/components/Title';
import AboutDesigner from 'shared/layouts/DetailsPage/components/ProductInfo/components/AboutDesigner';
import CustomerReview from 'shared/layouts/DetailsPage/components/ProductInfo/components/CustomerReview';
import SizeOptions from 'shared/layouts/DetailsPage/components/ProductInfo/components/SizeOptions';
import ColorSwatches from 'shared/layouts/DetailsPage/components/ProductInfo/components/ColorSwatches/Desktop';
import ActionContainer from 'shared/layouts/DetailsPage/components/ProductInfo/components/ActionContainer';
import AddToBag from 'shared/layouts/DetailsPage/components/ProductInfo/components/AddToBag';
import Notify from 'shared/components/Notify/Desktop';
import PriceWithDiscount from 'shared/layouts/DetailsPage/components/PriceWithDiscount';
import ProductOffers from 'shared/layouts/DetailsPage/components/ProductInfo/components/ProductOffers';
import DisclaimerText from 'shared/layouts/DetailsPage/components/ProductInfo/components/DisclaimerText';
import Multitag from 'shared/layouts/DetailsPage/components/ProductInfo/components/Multitag';
import ProductDescription from 'shared/layouts/DetailsPage/components/ProductInfo/components/ProductDescription';
import EcomPolicy from './components/EcomPolicy';
import PriceReveal from 'shared/layouts/DetailsPage/components/ProductInfo/components/PriceReveal/Desktop';
import WishlistButton from 'shared/layouts/DetailsPage/components/ProductInfo/Desktop/components/WishlistButton';
import RatingSummary from './components/RatingSummary';
import NewEcomPolicy from './components/NewEcomPolicy';
import BrandBook from 'shared/layouts/DetailsPage/components/ProductInfo/components/BrandBook';
import SizeInventory from 'shared/layouts/DetailsPage/components/ProductInfo/components/SizeOptions/components/SizeInventory';

// hooks
import { useProductInfo } from 'shared/layouts/DetailsPage/components/ProductInfo/hooks';
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';

// selectors
import { useSelectedSKUCode } from 'shared/store/details/hooks/selectors';

// styles
import {
  Actions,
  Container,
  CustomModelWidget,
  CustomWishlist,
  OutOfStockHelperText,
  PrimaryContent,
  Separator20,
  ProductOffersContainer,
  PromotionText,
} from './style';

// constants
import { USER_TYPE } from 'shared/layouts/DetailsPage/components/ProductInfo/constants';
import { TEXTS } from 'shared/components/Notify/constants';
const WISHLIST_ICON_SIZE = 24;
const CONTAINER_HEIGHTS = {
  COUPONS_OFFERS: 404,
  COUPONS: 196,
  OFFERS: 168,
  MARGIN: 40,
};

// analytics
import { trackNotifyMeClicked } from 'shared/layouts/Wishlist/analytics';

// defs
import { TagList } from 'shared/store/details/types';
import { CouponDetails, OfferDetails } from './types';
import { ITrackPrams } from '../Mobile/types';
import LoginNudgeTracker from 'shared/components/Nudge/LoginNudgeTracker';
import { PAGENAME } from 'shared/components/Nudge/constants';
interface IProductInfoProps {
  brandUrl: string;
  productID: string;
  badge?: ProductDetail.IBadge;
  disclaimer?: string;
  onIntlNetwork: boolean;
  priceRevealConfig: ProductDetail.IPriceRevealConfig;
  sizeOptions: ProductDetail.IProduct['sizeOptions'];
  type: string;
  categoryName: string;
  isOneSize: boolean;
  colorOptions: ProductDetail.IColorOption[];
  defaultColor: ProductDetail.IColor | null;
  isWishlistEnabled: boolean;
  tags: TagList;
  categoryLevels: {
    [key: string]: { id: string; name: string };
  };
  isProductOffersAvailable: boolean;
  productOffers: {
    offers: CouponDetails[];
    coupons: OfferDetails[];
  };
  isOffersFetching: boolean;
  newEcomFetchApiEnabled: boolean;
  brandBookData: {
    productID: string;
    widget_type: 'horizontal_widget' | 'review_tags_widget';
    title?: string;
  }[];
}
type BrandLaunchMetadata = {
  epoch_launch_date: number;
};

function ProductInfoDesktop({
  brandUrl,
  productID,
  badge,
  disclaimer,
  onIntlNetwork,
  priceRevealConfig,
  type,
  categoryName,
  isOneSize,
  colorOptions,
  defaultColor,
  isWishlistEnabled,
  tags,
  categoryLevels,
  isProductOffersAvailable,
  productOffers = { offers: [], coupons: [] },
  isOffersFetching,
  newEcomFetchApiEnabled,
  sizeOptions,
  brandBookData,
}: IProductInfoProps) {
  const {
    isAddedToCart,
    isOutOfStock,
    displayTitle,
    displaySubTitle,
    displayPrice,
    displayDiscount,
    displayDiscountedPrice,
    setSelectedSize,
    selectedSize,
    addToCart,
    priceRevealData,
    isSalePriceLive,
    brandLaunchingSoon: originalBrandLaunchingSoon,
    brandLaunchMetadata,
  } = useProductInfo();

  const specialBrandLaunchConfig = useRemoteConfig('specialBrandLaunch');
  const { variant } = getEligibleVariantName('add-to-cart-nudge');

  const cartNudgeEnabled = variant === 'atc-a';
  // Override brandLaunchingSoon based on config flag
  const brandLaunchingSoon = specialBrandLaunchConfig?.enabled ? originalBrandLaunchingSoon : false;
  const { showNudge: showCartNudge } = useCartNudge();
  const shownCartNudgeProducts = useRef<Set<string>>(new Set());
  const { pathname, search } = useLocation();

  const productSKU = useSelectedSKUCode();
  const [shakeSizeSelector, setShakeSizeSelector] = useState(false);

  const isBrandLaunchEnded = useMemo(() => {
    // Return false if the feature is disabled
    if (!specialBrandLaunchConfig?.enabled) {
      return false;
    }

    if (
      !brandLaunchMetadata ||
      !('epoch_launch_date' in brandLaunchMetadata) ||
      !brandLaunchMetadata.epoch_launch_date
    )
      return false;
    const now = Date.now() / 1000;
    return now > (brandLaunchMetadata as BrandLaunchMetadata)?.epoch_launch_date;
  }, [brandLaunchMetadata, specialBrandLaunchConfig?.enabled]);

  // Clean up notification after brand launch ends
  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      specialBrandLaunchConfig?.enabled &&
      isBrandLaunchEnded &&
      localStorage.getItem('brand_launch_notify')
    ) {
      localStorage.removeItem('brand_launch_notify');
    }
  }, [isBrandLaunchEnded, specialBrandLaunchConfig?.enabled]);

  const nudgeSizeSelector = () => {
    setShakeSizeSelector(false);
    setTimeout(() => {
      setShakeSizeSelector(true);
    }, 0);
  };
  const handleOnNotified = ({ email = '' }: ITrackPrams) => {
    trackNotifyMeClicked({
      email,
      productId: productID,
      price: String(displayPrice),
      isOutOfStock: true,
      discountedPrice: displayDiscountedPrice,
      discount: displayDiscount,
      type,
      categoryLevels,
      variantType: selectedSize?.sizeName,
      variantProductID: selectedSize?.id,
      productData: selectedSize,
      ...(badge?.title ? { productBadge: badge.title } : {}),
    });
  };

  const getAddToCartAction = useMemo(() => {
    return selectedSize === undefined ? nudgeSizeSelector : addToCart;
  }, [addToCart, selectedSize]);
  const selectedProductID = selectedSize !== undefined ? selectedSize.id : productID;
  const userType = onIntlNetwork ? USER_TYPE.INTERNATIONAL : USER_TYPE.DOMESTIC;

  const hasColors = isEmpty(defaultColor) === false && isEmpty(colorOptions) === false;

  const isPriceReveal = priceRevealConfig?.desktop && priceRevealData;

  const showPromotionText =
    priceRevealConfig?.desktop && priceRevealConfig?.salePromotionConfig.desktop && isSalePriceLive;

  const showPriceDetailsSeperator = hasColors || !isOneSize;

  const showColorsSizesSeperator = hasColors && !isOneSize;

  const wishlistMetaData = {
    productID: productID,
    categoryName: categoryName,
    discountPercent: displayDiscount,
    MRP: displayPrice,
    productName: displaySubTitle,
    productSKU: productSKU,
    discountedPrice: displayDiscountedPrice,
    productBrandName: displayTitle,
    isOutOfStock: Boolean(isOutOfStock),
    productTags: tags,
    categoryLevels,
    isPriceReveal: priceRevealConfig?.desktop ? !isEmpty(priceRevealData) : undefined,
    isSalePriceLive: showPromotionText,
    isProductOffersAvailable,
    ...(type ? { productType: type } : {}),
    ...(badge?.title ? { productBadge: badge.title } : {}),
    isCategoryLaunchingSoon: originalBrandLaunchingSoon,
  };

  const wishlistComponent = (
    <CustomWishlist
      size={WISHLIST_ICON_SIZE}
      productId={productID}
      canShowNotification={true}
      CustomWishlist={WishlistButton}
      trackingMetaData={wishlistMetaData}
    />
  );

  const offersContainerHeight = useMemo(() => {
    if (__SERVER__ || isOffersFetching) return CONTAINER_HEIGHTS.COUPONS_OFFERS;

    let height = 0;

    // container height for coupons
    if (productOffers.coupons?.length) {
      height += CONTAINER_HEIGHTS.COUPONS;
    }

    // add margin for whenever there are both coupons and offers
    if (productOffers.coupons?.length && productOffers.offers?.length) {
      height += CONTAINER_HEIGHTS.MARGIN;
    }

    // container height for offers
    if (productOffers.offers?.length) {
      height += CONTAINER_HEIGHTS.OFFERS;
    }

    return height;
  }, [productOffers, isOffersFetching]);


  useEffect(() => {
    shownCartNudgeProducts.current.clear();
  }, [pathname, search]);

  useEffect(() => {
    // Only show cart nudge if product is added to cart AND nudge hasn't been shown for this product yet
    if (isAddedToCart && !shownCartNudgeProducts.current.has(productSKU)) {
      showCartNudge();
      productSKU && shownCartNudgeProducts.current.add(productSKU);
    }
  }, [isAddedToCart, productSKU]);

  return (
    <Container
      showColorsSizesSeperator={showColorsSizesSeperator}
      isWishlistEnabled={isWishlistEnabled}
    >
      <ToastProvider>
        <LoginNudgeProvider page={PAGENAME.PDP}>
          <PrimaryContent>
            <LoginNudgeTracker pageType={PAGENAME.PDP} />
            <Multitag badge={badge} />
            <Title title={displayTitle} subTitle={displaySubTitle} actionUrl={brandUrl} />
            <RatingSummary />
            <PriceWithDiscount
              price={displayPrice}
              discount={displayDiscount}
              discountedPrice={displayDiscountedPrice}
            />
            {!isOutOfStock && isPriceReveal && (
              <PriceReveal
                priceRevealData={priceRevealData}
                priceRevealConfig={priceRevealConfig.pdp}
                productId={productID}
                wishlistMetaData={wishlistMetaData}
              />
            )}
            {showPromotionText && (
              <PromotionText promotionTextConfig={priceRevealConfig.salePromotionConfig.pdp} />
            )}
            {(isEmpty(sizeOptions) || isOneSize) && (
              <SizeInventory needTopMargin data-at="stock-warning" />
            )}
            {showPriceDetailsSeperator && <Separator20 />}
            <ColorSwatches />
            {showColorsSizesSeperator && <Separator20 />}
            <SizeOptions
              onSelect={setSelectedSize}
              currentSize={selectedSize}
              shake={shakeSizeSelector}
              isMobile={false}
              disabled={brandLaunchingSoon && !isBrandLaunchEnded}
            />
            <DisclaimerText disclaimer={disclaimer} data-test="pdp-disclaimer-text" />
            <CustomModelWidget />
            {isOutOfStock ? (
              <>
                <ActionContainer>
                  <Actions>
                    {wishlistComponent}
                    {!brandLaunchingSoon && (
                      <Notify productId={selectedProductID} onNotified={handleOnNotified} />
                    )}
                  </Actions>
                </ActionContainer>
                <OutOfStockHelperText>{TEXTS.NO_STOCK_MESSAGE}</OutOfStockHelperText>
              </>
            ) : (
              <ActionContainer>
                <Actions>
                  {wishlistComponent}
                  {!brandLaunchingSoon && (
                    <AddToBag
                      selectedSize={selectedSize}
                      isAddedToCart={isAddedToCart}
                      onClick={getAddToCartAction}
                      wishlistEnabled={false}
                      disabledOverride={true}
                      hideToast={cartNudgeEnabled}
                    />
                  )}
                </Actions>
              </ActionContainer>
            )}
            {!brandLaunchingSoon &&
              !isOutOfStock &&
              (userType === USER_TYPE.DOMESTIC && newEcomFetchApiEnabled ? (
                <NewEcomPolicy />
              ) : (
                <EcomPolicy />
              ))}
            {!brandLaunchingSoon && (
              <ProductOffersContainer height={offersContainerHeight}>
                <ProductOffers isDesktop />
              </ProductOffersContainer>
            )}
          </PrimaryContent>
          <ProductDescription />
          <CustomerReview />
        </LoginNudgeProvider>
        {brandBookData.length !== 0 ? <BrandBook /> : <AboutDesigner />}
      </ToastProvider>
    </Container>
  );
}

export default memo(ProductInfoDesktop);
